import { Component, Input, AfterViewInit, OnChanges, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import * as dayjs from 'dayjs';

import { Submission } from '../../model/paper';
import { DiscussionMessage } from '../../model/discussionMessage';

import { SubmissionsService } from 'src/app/service/submissions.service';
import { AuthService } from 'src/app/service/auth.service';
import { InfoService } from 'src/app/service/info.service';
import { NotificationService } from 'src/app/service/notification.service';
import { EventReviewConfiguration } from 'src/app/model/eventReviewConfiguration';

// Separador de dia acima de uma mensagem (estilo chat). 'today'/'yesterday' são
// traduzidos; 'date' formata via datePipe no template.
interface DaySeparator {
  kind: 'today' | 'yesterday' | 'date';
  date?: dayjs.Dayjs;
}

// Item decorado do chat: a mensagem + metadados de agrupamento/separador.
interface ChatItem {
  message: DiscussionMessage;
  own: boolean;
  firstOfGroup: boolean;          // 1ª de uma sequência do mesmo remetente/dia
  daySeparator: DaySeparator | null;
}

@Component({
  selector: 'app-discussion-messages',
  templateUrl: './discussion-messages.component.html',
  styleUrls: ['./discussion-messages.component.scss']
})
export class DiscussionMessagesComponent implements OnInit, OnChanges {
  @Input() submission: Submission;
  @Input() showAllSubmissions = false;
  @Input() reviewConfig: EventReviewConfiguration;
  @Input() eventTimeZone: string;

  @Input() userIsChairOrAdmin = false;

  public messages: Array<DiscussionMessage> = [];
  public chatItems: Array<ChatItem> = [];
  // Índice anônimo estável por remetente (não-own) → rótulo "Revisor #N" quando a
  // identidade é oculta. Atribuído na ordem de 1ª aparição em `rebuildChatItems`.
  private senderAnonIndex: { [id: string]: number } = {};
  // IDs de remetentes cuja foto (`imageAvatarUrl`) existe no banco mas falhou
  // ao carregar (404, arquivo removido, etc.) — sem isso o <img> ficava com o
  // ícone de imagem quebrada em vez de cair pras iniciais. Cada mensagem tem
  // seu próprio remetente, então o rastreio é por id (não uma flag única).
  private brokenAvatarSenderIds = new Set<number>();
  public form: FormGroup;
  public maxLength = 2000;
  public rows = 2;

  // Identidade do remetente visível? (não-autor + config de revisão "ALL".)
  public showIdentity = false;

  // Paleta para o nome/avatar do remetente (estilo grupo de WhatsApp: cada
  // pessoa ganha uma cor estável derivada do nome).
  private senderPalette = [
    '#1f7a8c', '#b5651d', '#6a4c93', '#2a7f62', '#a4303f',
    '#3d5a80', '#8a5a44', '#417b5a', '#7b466a', '#5c6b3c'
  ];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private infoService: InfoService,
    private submissionService: SubmissionsService,
    private notificationService: NotificationService,
  ) { }

  public ngOnInit(): void {
    if (this.submission) {
      this.messages = this.submission.discussionMessages;
      this.computeIdentityVisibility();
      this.rebuildChatItems();
      this.initForm();
    }
  }

  // Recalcula a visibilidade da identidade se `userIsChairOrAdmin`/`reviewConfig`
  // chegarem/mudarem depois do init (ambos são resolvidos async no paper-info).
  public ngOnChanges(): void {
    if (this.submission) {
      this.computeIdentityVisibility();
    }
  }

  // Constrói a lista decorada para o chat: ordena por data (mais antigas no
  // topo), marca a 1ª mensagem de cada sequência do mesmo remetente e insere
  // separadores quando muda o dia. Recalculado a cada mudança em `messages`.
  private rebuildChatItems(): void {
    const sorted = [...(this.messages || [])].sort(
      (a, b) => (a.createdAt?.valueOf() || 0) - (b.createdAt?.valueOf() || 0));

    // Índice anônimo por remetente (não-own), na ordem de 1ª aparição → "Revisor #N".
    this.senderAnonIndex = {};
    let nextAnon = 1;
    for (const message of sorted) {
      if (this.isOwn(message)) { continue; }
      const sid = message.sender?.id;
      if (sid != null && this.senderAnonIndex[sid] === undefined) {
        this.senderAnonIndex[sid] = nextAnon++;
      }
    }

    const items: ChatItem[] = [];
    let prev: DiscussionMessage | null = null;
    for (const message of sorted) {
      const own = this.isOwn(message);
      const newDay = !prev || !message.createdAt || !prev.createdAt
        || !message.createdAt.isSame(prev.createdAt, 'day');
      const key = own ? 'me' : (message.sender?.id ?? 'anon');
      const prevKey = prev ? (this.isOwn(prev) ? 'me' : (prev.sender?.id ?? 'anon')) : null;
      items.push({
        message,
        own,
        firstOfGroup: newDay || key !== prevKey,
        daySeparator: newDay ? this.dayLabel(message.createdAt) : null
      });
      prev = message;
    }
    this.chatItems = items;
  }

  private dayLabel(d: dayjs.Dayjs): DaySeparator {
    if (!d) { return { kind: 'date', date: d }; }
    const diff = dayjs().startOf('day').diff(d.startOf('day'), 'day');
    if (diff === 0) { return { kind: 'today' }; }
    if (diff === 1) { return { kind: 'yesterday' }; }
    return { kind: 'date', date: d };
  }

  // Índice anônimo estável do remetente (p/ "Revisor #N" quando identidade oculta).
  anonIndex(message: DiscussionMessage): number {
    return (message && message.sender && this.senderAnonIndex[message.sender.id]) || 0;
  }

  // Foto do remetente quando disponível (senão o template cai nas iniciais).
  // Retorna null também quando essa foto já falhou ao carregar antes (ver
  // `onAvatarError`), pra não ficar tentando renderizar um <img> quebrado.
  avatarUrl(message: DiscussionMessage): string | null {
    const senderId = message?.sender?.id;
    if (senderId != null && this.brokenAvatarSenderIds.has(senderId)) { return null; }
    return (message && message.sender && message.sender.profile
      && message.sender.profile.imageAvatarUrl) || null;
  }

  // Handler do (error) do <img> do avatar — marca o remetente como "sem foto
  // utilizável" pro restante da sessão, fazendo o template cair nas iniciais.
  onAvatarError(senderId: number | undefined): void {
    if (senderId != null) {
      this.brokenAvatarSenderIds.add(senderId);
    }
  }

  // Só revela o remetente para não-autores quando a config de revisão permite
  // (tpcViewReviewIdentity === 'ALL') — mesma regra que a tabela antiga usava.
  private computeIdentityVisibility(): void {
    // Chair/admin (interface do TPC chair) SEMPRE vê o nome dos revisores. Os
    // demais (não-autor) só quando a config libera (tpcViewReviewIdentity === 'ALL').
    this.showIdentity = this.userIsChairOrAdmin
      || (!this.submission.isAuthor(this.infoService.user.id)
          && this.reviewConfig?.tpcViewReviewIdentity === 'ALL');
  }

  // ── Suporte à visão de chat (estilo grupo de WhatsApp) ──────────────────────
  // Mensagem do próprio usuário logado → alinhada à direita (bolha "minha").
  isOwn(message: DiscussionMessage): boolean {
    const me = this.infoService.user;
    return !!(message && message.sender && me && message.sender.id === me.id);
  }

  // Chair/admin pode excluir (fora do modo "todas as submissões"). Espelha a
  // condição da coluna "delete" da tabela.
  get canDelete(): boolean {
    return this.userIsChairOrAdmin && !this.showAllSubmissions;
  }

  // Iniciais do remetente para o avatar (1–2 letras).
  initials(name: string): string {
    if (!name) { return '?'; }
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) || '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  }

  // Cor estável por remetente (hash simples do nome → índice na paleta).
  senderColor(name: string): string {
    if (!name) { return this.senderPalette[0]; }
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = (hash + name.charCodeAt(i)) % this.senderPalette.length;
    }
    return this.senderPalette[hash];
  }

  initForm() {
    this.form = this.fb.group({
      content: ['', [Validators.minLength(5), Validators.maxLength(this.maxLength)]]
    });
  }

  // Enter (sem modificador) envia; Shift+Enter quebra linha (Angular só casa
  // `keydown.enter` quando NÃO há modificadores, então Shift+Enter cai no
  // comportamento padrão do textarea). Sempre previne a nova linha no Enter puro.
  onComposerEnter(event: Event): void {
    event.preventDefault();
    if (this.form && this.form.valid && this.form.value['content'].length > 0) {
      this.submit();
    }
  }

  submit(): void {
    this.submissionService.postDiscussionMessages(this.submission.id, this.infoService.user.id, this.form.value['content'])
      .subscribe( messages => {
        this.messages = messages;
        this.rebuildChatItems();
        this.form.reset({content: ''});
    });
  }

  delete(message: DiscussionMessage): void {
    this.notificationService.openConfirmationDialog({title: 'submissions.discussion-messages.confirm-delete', content: ''})
      .subscribe( response => {
        if (response) {
          this.submissionService.deleteDiscussionMessage(this.submission.id, message.id).subscribe( messages => {
            this.messages.splice(this.messages.findIndex(m => m.id === message.id), 1);
            this.messages = [...this.messages];
            this.rebuildChatItems();
            this.notificationService.notify('submissions.discussion-messages.success-delete');
          }, error => {
            this.notificationService.notifyError('submissions.discussion-messages.fail-delete');
          });
        }
      });
  }
}
